import type { Audience, AuthorKind, PostKind, SocialPost } from '../types/social'

/** Human wording for each audience. Never abbreviate a privacy setting. */
export const AUDIENCE_LABEL: Record<Audience, string> = {
  private: 'Only you',
  connections: 'Your connections',
  group: 'Group members',
  community: 'Community members',
  campus: 'Your campus',
  institution: 'Everyone at your institution',
  public: 'Anyone on Campuzon',
}

export const POST_KIND_LABEL: Record<PostKind, string> = {
  text: 'Post',
  photo: 'Photo',
  poll: 'Poll',
  question: 'Question',
  recommendation: 'Recommendation',
  event: 'Event',
  study_group: 'Study group',
  listing: 'For sale',
  gig: 'Campus gig',
  announcement: 'Announcement',
}

/** Official voices are rendered differently. This is the single source of that. */
export function isOfficial(kind: AuthorKind): boolean {
  return kind === 'institution' || kind === 'faculty' || kind === 'department' || kind === 'campuzon'
}

export function relativeTime(iso: string, now = new Date('2026-08-14T10:00:00Z')): string {
  const minutes = Math.round((now.getTime() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export function pollTotal(post: SocialPost): number {
  if (!post.poll) return 0
  return post.poll.options.reduce((sum, option) => sum + option.votes, 0)
}

export function pollClosed(post: SocialPost, now = new Date('2026-08-14T10:00:00Z')): boolean {
  if (!post.poll) return false
  return new Date(post.poll.closesAt) < now
}

export function storyExpired(expiresAt: string, now = new Date('2026-08-14T10:00:00Z')): boolean {
  return new Date(expiresAt) < now
}

/**
 * Campus-aware ordering. Official notices first, then anything unresolved that
 * the reader can actually help with, then recency. Deliberately not an
 * engagement ranking — reaction counts never move a post up.
 */
export function orderFeed(posts: SocialPost[]): SocialPost[] {
  const rank = (post: SocialPost): number => {
    if (isOfficial(post.author.kind)) return 0
    if (post.kind === 'question' && !post.resolved && post.answers.length === 0) return 1
    if (post.kind === 'poll') return 2
    return 3
  }
  return [...posts].sort((a, b) => {
    const byRank = rank(a) - rank(b)
    if (byRank !== 0) return byRank
    return new Date(b.at).getTime() - new Date(a.at).getTime()
  })
}

/** Comment count that excludes moderated-away content. */
export function visibleComments(post: SocialPost) {
  return post.comments.filter((comment) => !comment.removed)
}

/** Extract @handles so a mention can be resolved rather than guessed. */
export function extractMentions(body: string): string[] {
  return [...body.matchAll(/@([a-z0-9._-]+)/gi)].map((match) => match[1].toLowerCase())
}
